import cn from 'classnames';
import type { MouseEvent} from 'react';
import { useEffect, useRef, useState } from 'react';
import { productGuideSearch } from '../api/search';
import { site } from '../config';
import Page from '../generic/Page';
import { WrapTrackingContext } from '../generic/TrackingContext';
import useUser from '../login/userContext';
import type { Breadcrumb, Facet as FacetProps} from '../model/Facets';
import { configureProductGuideFacets, getQuery } from '../model/Facets';
import PriceRangeFacet from '../search/PriceRangeFacet';
import StoreFacet from '../search/StoreFacet';
import type { Coords } from '../storelocator/StoreLocatorForm';
import { scrollToTop } from '../utils/documentUtils';
import { trackLinkClick } from '../utils/trackLinkClick';
import { setUrl } from '../utils/url';
import { useAsyncAction } from '../utils/useAsyncAction';
import useOnMount from '../utils/useOnMount';
import { SelectedFacet, UnselectedFacet } from './Facet';
import FacetList from './FacetList';
import * as style from './ProductGuide.module.css';

export default function ProductGuide() {
    const productGuideRef = useRef<HTMLDivElement>(null);
    const { user } = useUser();

    const [facets, setFacets] = useState<FacetProps[]>();
    const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>();
    const [currentQuery, setCurrentQuery] = useState<string>();
    const [expandedFacet, setExpandedFacet] = useState('isGoodfor');
    const [warningMessage, setWarningMessage] = useState<string>();

    const [getFacets, isLoading, error] = useAsyncAction(async (queryString?: string, queryParams?: Coords) => {
        const data = await productGuideSearch(queryString, queryParams);
        const { facets, anyFacets, breadcrumbs } = configureProductGuideFacets(data, user);
        setFacets(facets);
        setBreadcrumbs(breadcrumbs);
        setCurrentQuery(getQuery(data.currentQuery));
        setExpandedFacet(getNextFacet(facets));
        setWarningMessage(anyFacets ? undefined : 'Vi fant ingen produkter som passer.');
    }, true);

    useOnMount(() => {
        const query = new URLSearchParams(location.search).get('q') ?? undefined;
        getFacets(query);
    });

    useEffect(() => {
        if (!currentQuery) return;
        if (currentQuery === ':relevance') return;
        setUrl(`q=${currentQuery}`, true);
    }, [currentQuery]);

    const onClickFacet = async (facetQuery: string) => {
        await getFacets(facetQuery);
        scrollToTop(productGuideRef.current);
    };

    const goToSearchResults = (event: MouseEvent<HTMLElement>) => {
        trackLinkClick(event);
        const query = currentQuery ? currentQuery : '';
        if (!!breadcrumbs?.length) {
            window.location.href = site.searchURL(query);
        } else {
            setWarningMessage('Fortell oss hva du ønsker ved å ta minst ett valg over.');
        }
    };

    return (
        <Page title="Drikkevelger" isSpinning={isLoading} error={error}>
            {!!facets && !!breadcrumbs && (
                <div className={style.productGuide} ref={productGuideRef}>
                    {breadcrumbs?.map((breadcrumb) => (
                        <SelectedFacet key={breadcrumb.facetCode} breadcrumb={breadcrumb} removeFacetValue={onClickFacet} />
                    ))}
                    {facets.map((facet) => (
                        <UnselectedFacet key={facet.code} facet={facet} toggleExpand={setExpandedFacet} isExpanded={expandedFacet === facet.code}>
                            <WrapTrackingContext set={{ filter_type: facet.name }}>
                                {facet.code === 'availableInStores' && (
                                    <StoreFacet onLocationChange={(location) => getFacets(currentQuery, location)} />
                                )}
                                {facet.userInputtedInterval ? (
                                    <PriceRangeFacet facet={facet} onFacetValueSelect={onClickFacet} />
                                ) : (
                                    <FacetList facet={facet} onClick={onClickFacet} />
                                )}
                            </WrapTrackingContext>
                        </UnselectedFacet>
                    ))}
                    <div className={style.stickyFooter}>
                        {warningMessage ? (
                            <p className={style.warningMsg}>{warningMessage}</p>
                        ) : (
                            <button className={cn(style.btn, 'btn-primary')} onClick={goToSearchResults}>
                                Se drikkeforslag
                            </button>
                        )}
                    </div>
                </div>
            )}
        </Page>
    );
}

function getNextFacet(facets: FacetProps[]) {
    return facets[0]?.code ?? '';
}
